import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import dayjs from 'dayjs';
import { FiRefreshCw, FiClock, FiMapPin } from 'react-icons/fi';
import * as slotSettingActions from '../../Redux/Actions/slotSettings';
import { SectionCard, PageLoader, ErrorState, EmptyState, AlertBox } from './common';
import { CenterSelect, DateField, SelectField } from './fields';
import { toCenterId, getErrorMessage, formatDate, formatTimeRange, formatClosedRange } from './helpers';

const STATUS_OPTIONS = [
    { value: 'OPEN', label: 'Open slots (customer view)' },
    { value: 'HOLD', label: 'On hold' }
];

const GENDER_OPTIONS = [
    { value: '', label: 'Any (all seats)' },
    { value: 'Male', label: 'Male applicant' },
    { value: 'Female', label: 'Female applicant' }
];

const PERIODS = [
    { key: 'morning', label: 'Morning', test: hour => hour < 12 },
    { key: 'afternoon', label: 'Afternoon', test: hour => hour >= 12 && hour < 17 },
    { key: 'evening', label: 'Evening', test: hour => hour >= 17 }
];

const slotTime = (value) => {
    const match = /T(\d{1,2})[.:](\d{2})/.exec(value || '');
    return match ? `${match[1].padStart(2, '0')}:${match[2]}` : '--:--';
};

const quickDates = () => [
    { label: 'Today', value: dayjs().format('YYYY-MM-DD') },
    { label: 'Tomorrow', value: dayjs().add(1, 'day').format('YYYY-MM-DD') },
    { label: 'In 7 days', value: dayjs().add(7, 'day').format('YYYY-MM-DD') },
    { label: 'In 30 days', value: dayjs().add(30, 'day').format('YYYY-MM-DD') }
];

const SlotPreviewTab = ({ centers, centersLoading, centersError, onRetryCenters, refreshKey }) => {
    const [centerId, setCenterId] = useState('');
    const [date, setDate] = useState(dayjs().add(1, 'day').format('YYYY-MM-DD'));
    const [slotStatus, setSlotStatus] = useState('OPEN');
    const [gender, setGender] = useState('');
    const [result, setResult] = useState(null);
    const [apiMessage, setApiMessage] = useState('');
    const [dayInfo, setDayInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const requestId = useRef(0);

    useEffect(() => {
        if (centerId === '' && centers.length) setCenterId(String(centers[0].value));
    }, [centers, centerId]);

    const loadSlots = useCallback(() => {
        if (centerId === '' || !date) return;
        const current = ++requestId.current;
        setLoading(true);
        setLoadError('');
        const id = toCenterId(centerId);
        const dayRequest = slotSettingActions.getCalendar(id, date.substring(0, 7))
            .then(res => (res?.days || []).find(d => d.date === date) || null)
            .catch(() => null);
        Promise.all([slotSettingActions.getSlots({ centerId: id, date, slotStatus, gender }), dayRequest]).then(([res, day]) => {
            if (current !== requestId.current) return;
            setResult(res?.data || res || null);
            setApiMessage(typeof res?.message === 'string' ? res.message.trim() : '');
            setDayInfo(day);
            setLoading(false);
        }).catch(err => {
            if (current !== requestId.current) return;
            setResult(null);
            setApiMessage('');
            setDayInfo(null);
            setLoading(false);
            setLoadError(getErrorMessage(err, 'Unable to load slots'));
        });
    }, [centerId, date, slotStatus, gender]);

    useEffect(() => {
        loadSlots();
    }, [loadSlots, refreshKey]);

    const hasReason = !!apiMessage && apiMessage.toLowerCase() !== 'success';
    const centerCapacity = result?.center?.config?.slotMaxSeats || 0;
    const capacityOf = (slot) => (slot.totalSeats !== null && slot.totalSeats !== undefined ? slot.totalSeats : centerCapacity);
    const slots = (result?.slots || [])
        .map(slot => ({ ...slot, from: slotTime(slot.start), to: slotTime(slot.end) }))
        .sort((a, b) => a.from.localeCompare(b.from));
    const onHold = slots.filter(s => s.status === 'on_hold').length;
    const booked = slots.reduce((sum, s) => sum + (s.occupiedSeats || 0), 0);
    const freeSeats = slots.some(s => capacityOf(s))
        ? slots.reduce((sum, s) => sum + Math.max(capacityOf(s) - (s.occupiedSeats || 0), 0), 0)
        : null;
    const hasCounters = slots.some(slot => slot.totalSeats !== null && slot.totalSeats !== undefined);
    const seatsFor = { '': 'all counters', Male: 'Male and Both counters', Female: 'Female and Both counters' }[gender];
    const centerLabel = centers.find(c => String(c.value) === centerId)?.label || '';

    const renderDayInfo = () => {
        if (!dayInfo) return null;
        if (dayInfo.isClosed) {
            return <AlertBox variant="warning" title={`${formatDate(date)} is closed for booking`} messages={dayInfo.reason || 'Closed'} />;
        }
        const hours = dayInfo.openFrom || dayInfo.openTo ? `Open ${formatTimeRange(dayInfo.openFrom, dayInfo.openTo)}` : 'Open all day (all template slots)';
        const closed = (dayInfo.closedRanges || []).map(range => `Closed ${formatClosedRange(range)}`);
        return <AlertBox variant="info" title={`${dayInfo.dayOfWeek}, ${formatDate(date)}: ${hours}`} messages={closed} />;
    };

    const renderSlots = () => {
        if (centersLoading) return <PageLoader text="Loading centers..." />;
        if (centersError) return <ErrorState title="Could not load centers" message={centersError} onRetry={onRetryCenters} />;
        if (!centers.length) return <EmptyState icon={<FiMapPin />} title="No active centers" text="There are no active centers to check." />;
        if (loading) return <PageLoader text="Loading slots..." />;
        if (loadError) return <ErrorState title="Could not load slots" message={loadError} onRetry={loadSlots} />;
        if (!result) return null;

        if (slots.length === 0) {
            const fallback = dayInfo?.isClosed
                ? `No slots available: ${dayInfo.reason}`
                : 'GetSlots returned no slots. Check the weekly schedule, holidays, counters and slot templates for this center.';
            return (
                <EmptyState
                    icon={<FiClock />}
                    title="No slots for this date"
                    text={<span className="ss-api-message">{hasReason ? apiMessage : fallback}</span>}
                />
            );
        }

        return (
            <>
                {renderDayInfo()}
                {hasCounters && (
                    <AlertBox variant="info" messages={`This center uses counters: seats at each time = ${seatsFor} open for that whole slot (1 counter = 1 seat).`} />
                )}
                <div className="ss-stats">
                    <div className="ss-stat">
                        <span className="ss-stat-label">Slots returned</span>
                        <span className="ss-stat-value">{slots.length}</span>
                    </div>
                    <div className="ss-stat">
                        <span className="ss-stat-label">On hold</span>
                        <span className="ss-stat-value">{onHold}</span>
                    </div>
                    <div className="ss-stat">
                        <span className="ss-stat-label">Seats booked</span>
                        <span className="ss-stat-value">{booked}</span>
                    </div>
                    <div className="ss-stat">
                        <span className="ss-stat-label">Seats free{!hasCounters && centerCapacity ? ` (${centerCapacity} per slot)` : ''}</span>
                        <span className="ss-stat-value">{freeSeats === null ? '-' : freeSeats}</span>
                    </div>
                </div>

                {PERIODS.map(period => {
                    const list = slots.filter(slot => period.test(Number(slot.from.substring(0, 2))));
                    if (!list.length) return null;
                    return (
                        <div key={period.key} className="ss-period-group">
                            <h6 className="ss-period">{period.label} · {list.length} slot{list.length > 1 ? 's' : ''}</h6>
                            <div className="ss-slot-grid">
                                {list.map(slot => {
                                    const capacity = capacityOf(slot);
                                    const used = slot.occupiedSeats || 0;
                                    const full = capacity > 0 && used >= capacity;
                                    const hold = slot.status === 'on_hold';
                                    const kind = hold ? 'is-hold' : full ? 'is-full' : '';
                                    const pill = hold ? 'is-custom' : full ? 'is-holiday' : 'is-open';
                                    const label = hold ? 'On hold' : full ? 'Full' : 'Available';
                                    return (
                                        <div key={slot.id || slot.start} className={`ss-slot ${kind}`}>
                                            <div className="ss-slot-head">
                                                <span className="ss-slot-time">{formatTimeRange(slot.from, slot.to)}</span>
                                                <span className={`ss-pill ${pill}`}>{label}</span>
                                            </div>
                                            <div className="ss-slot-seats">
                                                <span>{used} booked{hasCounters ? ` of ${capacity}` : ''}</span>
                                                {capacity > 0 && <span>{Math.max(capacity - used, 0)} free</span>}
                                            </div>
                                            {capacity > 0 && (
                                                <div className="ss-bar"><span style={{ width: `${Math.min((used / capacity) * 100, 100)}%` }} /></div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </>
        );
    };

    return (
        <>
            <SectionCard
                title="Check slots"
                subtitle="Calls the live GetSlots API, so you see exactly what customers see after your settings."
            >
                <div className="ss-preview-toolbar">
                    <div>
                        <Form.Label htmlFor="sp-center">Center</Form.Label>
                        <CenterSelect id="sp-center" value={centerId} centers={centers} loading={centersLoading}
                            disabled={centersLoading || !centers.length} onChange={setCenterId} />
                    </div>
                    <div>
                        <Form.Label htmlFor="sp-date">Date</Form.Label>
                        <DateField id="sp-date" value={date} allowClear={false} onChange={value => value && setDate(value)} />
                    </div>
                    <div>
                        <Form.Label htmlFor="sp-status">Slot status</Form.Label>
                        <SelectField id="sp-status" value={slotStatus} options={STATUS_OPTIONS} onChange={setSlotStatus} />
                    </div>
                    <div>
                        <Form.Label htmlFor="sp-gender">Applicant</Form.Label>
                        <SelectField id="sp-gender" value={gender} options={GENDER_OPTIONS} onChange={setGender} />
                    </div>
                    <div>
                        <Button variant="outline-primary" disabled={loading || centerId === '' || !date} onClick={loadSlots}>
                            <FiRefreshCw /> Refresh
                        </Button>
                    </div>
                </div>
                <div className="ss-quick-dates">
                    {quickDates().map(q => (
                        <button key={q.label} type="button" className={`ss-chip ${date === q.value ? 'active' : ''}`} onClick={() => setDate(q.value)}>
                            {q.label}
                        </button>
                    ))}
                </div>
            </SectionCard>

            <SectionCard
                title={centerLabel && date ? `${centerLabel} · ${dayjs(date).format('dddd, DD MMM YYYY')}` : 'Slots'}
                subtitle="Fully booked slots are not returned by GetSlots."
            >
                {renderSlots()}
            </SectionCard>
        </>
    );
};

export default SlotPreviewTab;
